import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import {
  updateConfig,
  getDeliveryChallanEditData,
} from "../../../store/slices/Delivery/deliveryChallanSlice";
import { debounce } from "lodash";
import { useLocation } from "react-router-dom";
import {
  useGetVerifiedStocksListDataQuery,
  useGetDeliveryChallanUploadsListDataQuery,
} from "../../../store/queries/Delivery";
import { getDeliveryChallanPrint, updateDeliveryChallanStatus } from "./api";
import { toast } from "react-toastify";
import html2pdf from "html2pdf.js";
import moment from "moment";
import { subDays } from "date-fns";

const useDeliveryChallan = () => {
  let paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];

  const [loadingChellan, setLoadingChellan] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const chellanRef = useRef(null);
  const dispatch = useDispatch();
  const reduxState = useSelector((state) => state.deliveryChallan);
  const queryState = reduxState?.Tab;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const activeTab = searchParams.get("activeTab") || "delivery-challan";
  const { tableFields, showEditModal } = useSelector((state) => state.global);
  const [date, setDate] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
    key: "selection",
  });
  const startDate = new Date(date.startDate).toISOString().split("T")[0];
  const endDate = new Date(date.endDate).toISOString().split("T")[0];
  const initialFormattedDate = [startDate, endDate];
  const [formattedDate, setFormattedDate] = useState(initialFormattedDate);

  const isTab1 = activeTab === "delivery-challan";

  const {
    data: verifiedStocksList = {},
    isFetching: isFetchingTab1,
    refetch: refetchTab1,
  } = useGetVerifiedStocksListDataQuery(
    {
      page_size: queryState?.currentPageSize,
      page: queryState?.currentPage,
      sort_by: queryState?.sortBy,
      sort_order: queryState?.sortOrder,
      search: queryState?.search,
      start: formattedDate[0],
      end: formattedDate[1],
    },
    { skip: !isTab1 },
  );
  const {
    data: challanUploadsList = {},
    isFetching: isFetchingTab2,
    refetch: refetchTab2,
  } = useGetDeliveryChallanUploadsListDataQuery(
    {
      page_size: queryState?.currentPageSize,
      page: queryState?.currentPage,
      sort_by: queryState?.sortBy,
      sort_order: queryState?.sortOrder,
      search: queryState?.search,
      start: formattedDate[0],
      end: formattedDate[1],
    },
    { skip: isTab1 },
  );

  const currentData = isTab1 ? verifiedStocksList : challanUploadsList;
  const isFetching = isTab1 ? isFetchingTab1 : isFetchingTab2;
  const refetch = isTab1 ? refetchTab1 : refetchTab2;

  useEffect(() => {
    dispatch(
      globalUpdateConfig((state) => {
        state.tableFields = currentData?.data?.fields;
      }),
    );
    // eslint-disable-next-line
  }, [currentData]);

  useEffect(() => {
    const updateState = () => {
      return new Promise((resolve) => {
        dispatch(
          updateConfig((state) => {
            state.Tab.activeTab = activeTab;
            state.Tab.clear = true;
            state.Tab.currentPage = 1;
          }),
        );
        resolve();
      });
    };
    updateState().then(() => {
      dispatch(
        updateConfig((state) => {
          state.Tab.clear = false;
        }),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const getRow = (feild, data) => {
    const rows = {
      common: (feild, data) => <p className="pro-mb-0">{data[feild]}</p>,
      gli_number: (field, data) => {
        return Array.isArray(data[field]) ? (
          data[field]?.map((gli, index) => (
            <span
              key={index}
              className="pro-badge badge-primary"
              style={{
                padding: "4px 8px",
                borderRadius: "15px",
                backgroundColor: "var(--pro-primary)",
                color: "#fff",
                fontSize: "12px",
                marginRight: "4px",
              }}
            >
              {gli}
            </span>
          ))
        ) : (
          <p className="pro-mb-0">{data[field]}</p>
        );
      },
      status: (_, data) =>
        data?.status === "CANCELED" ? (
          <span className={"pro-badge badge-danger-outline pro-d-inline-block"}>
            {"CANCELED"}
          </span>
        ) : data?.status === "COMPLETED" ? (
          <span className={"outline-success pro-badge pro-d-inline-block"}>
            {"COMPLETED"}
          </span>
        ) : (
          <span className={"outline pro-badge pro-d-inline-block"}>
            {data?.status}
          </span>
        ),
    };
    return rows?.[feild]?.(feild, data) ?? rows["common"](feild, data);
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      }),
    );
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.Tab.currentPageSize = page_size;
        state.Tab.currentPage = 1;
      }),
    );
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.Tab.currentPage = page;
      }),
    );
  };

  const handleSort = (label) => {
    if (queryState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.Tab.currentPage = 1;
          state.Tab.sortOrder = queryState.sortOrder === "asc" ? "desc" : "asc";
        }),
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.Tab.currentPage = 1;
          state.Tab.sortBy = label;
          state.Tab.sortOrder = "asc";
        }),
      );
    }
  };

  const handleSearchDebounced = debounce((value, dispatch) => {
    dispatch(
      updateConfig((state) => {
        state.Tab.search = value;
        state.Tab.currentPage = 1;
      }),
    );
  }, 1000);

  const handleSearch = (e) => {
    handleSearchDebounced(e.target.value, dispatch);
  };

  const handlePrepare = (selectedIds) => {
    const selectedData = currentData?.data?.data?.filter((item) =>
      selectedIds?.includes(item?.id),
    );
    const gliNumbers = selectedData?.map((item) => item?.gli_number) || [];
    dispatch(
      updateConfig((state) => {
        state.showOffcanvas = true;
        state.isEdit = false;
        state.selectedRows = gliNumbers;
      }),
    );
  };
  const handleCancel = (selectedIds) => {
    setCancelReason("");
    setCancelReasonError("");
    dispatch(
      updateConfig((state) => {
        state.showConfirmationModal = !state.showConfirmationModal;
        if (selectedIds) {
          state.selectedCancelIds = selectedIds;
        }
      }),
    );
  };

  const handleCancelReasonChange = (e) => {
    setCancelReason(e.target.value);
    if (e.target.value?.trim()) {
      setCancelReasonError("");
    }
  };

  const handleStatusChange = async () => {
    const trimmedReason = cancelReason?.trim();
    if (!trimmedReason) {
      setCancelReasonError("Enter reason for cancellation");
      return;
    }
    const selectedIds = reduxState?.selectedCancelIds || [];
    try {
      for (const id of selectedIds) {
        const res = await updateDeliveryChallanStatus(id, trimmedReason);
        if (res?.status === 200) {
          toast.success(res?.data?.message || "Status updated successfully");
        } else {
          toast.error(res?.message || "Failed to update status");
        }
      }
      refetch();
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setCancelReason("");
      setCancelReasonError("");
      dispatch(
        updateConfig((state) => {
          state.showConfirmationModal = false;
          state.selectedCancelIds = [];
        }),
      );
    }
  };

  const handleEdit = (selectedIds) => {
    const id = selectedIds?.[0];
    dispatch(
      updateConfig((state) => {
        state.showOffcanvas = true;
        state.isEdit = true;
        state.selectedEditId = id;
      }),
    );
    dispatch(getDeliveryChallanEditData({ id }));
  };

  const handleCloseOffcanvas = () => {
    dispatch(
      updateConfig((state) => {
        state.showOffcanvas = false;
        state.isEdit = false;
        state.editData = {};
        state.selectedRows = [];
        state.selectedEditId = null;
      }),
    );
  };

  const handlePrintChellan = (e) => {
    setLoadingChellan(true);
    const id = e?.[0];
    getDeliveryChallanPrint(id).then((response) => {
      if (response?.data?.status_code === 200) {
        dispatch(
          updateConfig(
            (state) => (state.printChellanData = response?.data?.data),
          ),
        );
        setLoadingChellan(false);
        toast.success("Delivery Challan downloaded successfully");
      } else {
        setLoadingChellan(false);
        toast.error("Something went wrong");
      }
      const printChellan = () => {
        const element = chellanRef?.current;
        html2pdf(element, {
          margin: [10, 2, 2, 2],
          filename: `${
            "Delivery Challan" +
            "-" +
            moment(Date.now()).format("_MM_DD_YYYY_HH_mm_ss")
          }.pdf`,
          image: { type: "jpeg", quality: 1 },
          html2canvas: {
            dpi: 100,
            letterRendering: true,
            useCORS: true,
            logging: true,
            scale: 4,
            scrollY: 0,
          },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
          DisablePdfCompression: true,
        });
      };
      setTimeout(() => {
        printChellan();
      }, 500);
    });
  };
  const handleDateChange = (date) => {
    const startDateUTC = new Date(date.startDate);
    const endDateUTC = new Date(date.endDate);
    const timeZoneOffset = 5.5 * 60 * 60 * 1000;
    const startDateIST = new Date(startDateUTC.getTime() + timeZoneOffset);
    const endDateIST = new Date(endDateUTC.getTime() + timeZoneOffset);

    const formatDate = (date) => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    const startDateFormatted = formatDate(startDateIST);
    const endDateFormatted = formatDate(endDateIST);
    const dateArray = [startDateFormatted, endDateFormatted];
    setFormattedDate(dateArray);
  };

  const navigation = [
    {
      label: "Delivery Challan",
      title: "Delivery Challan",
      link: `/orders/delivery/delivery-challan?activeTab=delivery-challan`,
      active: activeTab === "delivery-challan",
    },
    {
      label: "Delivery Challan Uploads",
      title: "Delivery Challan Uploads",
      link: `/orders/delivery/delivery-challan?activeTab=delivery-challan-uploads`,
      active: activeTab === "delivery-challan-uploads",
    },
  ];

  return {
    date,
    tabNavs: navigation,
    activeTab,
    isTab1,
    paginationOptions,
    currentData,
    isFetching,
    tableFields,
    showEditModal,
    queryState,
    reduxState,
    showOffcanvas: reduxState?.showOffcanvas,
    handleSearch,
    handleSort,
    handlePageSize,
    handlePagination,
    handleEditColumnsClick,
    refetch,
    getRow,
    handlePrepare,
    handleEdit,
    handleCloseOffcanvas,
    handleCancel,
    handleStatusChange,
    cancelReason,
    cancelReasonError,
    handleCancelReasonChange,
    handlePrintChellan,
    chellanRef,
    loadingChellan,
    handleDateChange,
  };
};

export default useDeliveryChallan;
